import { lazy, Suspense, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/context/ThemeContext';
import PageLayout from '@/components/layout/PageLayout';
import PlausibleAnalytics from '@/components/shared/PlausibleAnalytics';

// Priority pages
const Home = lazy(() => import('@/pages/Home'));
const LaunchAnMVNO = lazy(() => import('@/pages/LaunchAnMVNO'));
const LaunchAspiring = lazy(() => import('@/pages/LaunchAspiring'));
const LaunchExisting = lazy(() => import('@/pages/LaunchExisting'));
const LaunchMobileBrand = lazy(() => import('@/pages/LaunchMobileBrand'));
const SolutionsBanks = lazy(() => import('@/pages/SolutionsBanks'));
const PlatformHub = lazy(() => import('@/pages/PlatformHub'));
const PlatformCore = lazy(() => import('@/pages/PlatformCore'));

// Platform pages
const PlatformBSS = lazy(() => import('@/pages/PlatformBSS'));
const PlatformActivate = lazy(() => import('@/pages/PlatformActivate'));
const PlatformMarketplace = lazy(() => import('@/pages/PlatformMarketplace'));

// Solutions pages
const SolutionsConsumer = lazy(() => import('@/pages/SolutionsConsumer'));
const SolutionsEnterprise = lazy(() => import('@/pages/SolutionsEnterprise'));
const SolutionsDiaspora = lazy(() => import('@/pages/SolutionsDiaspora'));
const SolutionsIoT = lazy(() => import('@/pages/SolutionsIoT'));
const SolutionsForeignMVNO = lazy(() => import('@/pages/SolutionsForeignMVNO'));

// Regulatory pages
const Regulatory = lazy(() => import('@/pages/Regulatory'));
const RegulatoryNCC = lazy(() => import('@/pages/RegulatoryNCC'));
const RegulatoryNDPA = lazy(() => import('@/pages/RegulatoryNDPA'));
const RegulatorySIM = lazy(() => import('@/pages/RegulatorySIM'));
const RegulatoryLawful = lazy(() => import('@/pages/RegulatoryLawful'));

// Supporting pages
const Partners = lazy(() => import('@/pages/Partners'));
const Trust = lazy(() => import('@/pages/Trust'));
const AboutHub = lazy(() => import('@/pages/about/AboutHub'));
const AboutWhoWeAre = lazy(() => import('@/pages/about/AboutWhoWeAre'));
const AboutWhatWeDo = lazy(() => import('@/pages/about/AboutWhatWeDo'));
const AboutHowWeDoIt = lazy(() => import('@/pages/about/AboutHowWeDoIt'));
const AboutCompanyFacts = lazy(() => import('@/pages/about/AboutCompanyFacts'));
const AboutLeadership = lazy(() => import('@/pages/about/AboutLeadership'));
const AboutLeadershipBoard = lazy(() => import('@/pages/about/AboutLeadershipBoard'));
const AboutLeadershipExec = lazy(() => import('@/pages/about/AboutLeadershipExec'));
const AboutLeadershipBio = lazy(() => import('@/pages/about/AboutLeadershipBio'));
const Contact = lazy(() => import('@/pages/Contact'));
const BookACall = lazy(() => import('@/pages/BookACall'));
const BookACallConfirmation = lazy(() => import('@/pages/BookACallConfirmation'));
const NewsAndInsightsHub = lazy(() => import('@/pages/NewsAndInsightsHub'));
const NewsAndInsightsArticle = lazy(() => import('@/pages/NewsAndInsightsArticle'));

// Legal pages
const Legal = lazy(() => import('@/pages/Legal'));
const LegalPrivacy = lazy(() => import('@/pages/LegalPrivacy'));
const LegalTerms = lazy(() => import('@/pages/LegalTerms'));
const LegalCookie = lazy(() => import('@/pages/LegalCookie'));
const LegalAccessibility = lazy(() => import('@/pages/LegalAccessibility'));

// 404
const NotFound = lazy(() => import('@/pages/NotFound'));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer until after route content paints so the anchor exists.
      const id = hash.slice(1);
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempt < 12) {
          setTimeout(() => tryScroll(attempt + 1), 60);
        }
      };
      tryScroll();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-mcx-paper">
    <div className="flex flex-col items-center gap-4">
      <img src="/mcx-logo-full-240.png" alt="MobileCoreX" className="h-9 w-auto object-contain" />
      <p className="text-sm text-mcx-slate">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <PageLayout>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/launch-an-mvno" element={<LaunchAnMVNO />} />
          <Route path="/launch-an-mvno/aspiring-mvno" element={<LaunchAspiring />} />
          <Route path="/launch-an-mvno/existing-licensee" element={<LaunchExisting />} />
          <Route path="/launch-an-mvno/mobile-brand" element={<LaunchMobileBrand />} />
          <Route path="/solutions/banks-and-fintechs" element={<SolutionsBanks />} />
          <Route path="/platform" element={<PlatformHub />} />
          <Route path="/platform/mcx-core" element={<PlatformCore />} />
          <Route path="/platform/mcx-bss" element={<PlatformBSS />} />
          <Route path="/platform/mcx-activate" element={<PlatformActivate />} />
          <Route path="/platform/mcx-marketplace" element={<PlatformMarketplace />} />
          <Route path="/platform/mcx-launchpath" element={<Navigate to="/launch-an-mvno#launchpath-programme" replace />} />
          <Route path="/solutions/consumer-brands" element={<SolutionsConsumer />} />
          <Route path="/solutions/enterprise-and-corporate-affinity" element={<SolutionsEnterprise />} />
          <Route path="/solutions/diaspora-and-affinity" element={<SolutionsDiaspora />} />
          <Route path="/solutions/iot-and-m2m" element={<SolutionsIoT />} />
          <Route path="/solutions/foreign-mvno-landing" element={<SolutionsForeignMVNO />} />
          <Route path="/regulatory" element={<Regulatory />} />
          <Route path="/regulatory/ncc-mvno-framework" element={<RegulatoryNCC />} />
          <Route path="/regulatory/ndpa-and-data-protection" element={<RegulatoryNDPA />} />
          <Route path="/regulatory/sim-registration-and-kyc" element={<RegulatorySIM />} />
          <Route path="/regulatory/lawful-intercept" element={<RegulatoryLawful />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/about" element={<AboutHub />} />
          <Route path="/about/who-we-are" element={<AboutWhoWeAre />} />
          <Route path="/about/what-we-do" element={<AboutWhatWeDo />} />
          <Route path="/about/how-we-do-it" element={<AboutHowWeDoIt />} />
          <Route path="/about/company-facts" element={<AboutCompanyFacts />} />
          <Route path="/about/leadership" element={<AboutLeadership />} />
          <Route path="/about/leadership/board-of-directors" element={<AboutLeadershipBoard />} />
          <Route path="/about/leadership/board-of-directors/:slug" element={<AboutLeadershipBio group="board-of-directors" />} />
          <Route path="/about/leadership/executive-officers" element={<AboutLeadershipExec />} />
          <Route path="/about/leadership/executive-officers/:slug" element={<AboutLeadershipBio group="executive-officers" />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/book-a-call" element={<BookACall />} />
          <Route path="/book-a-call/confirmation" element={<BookACallConfirmation />} />
          <Route path="/insights" element={<Navigate to="/news-and-insights" replace />} />
          <Route path="/news-and-insights" element={<NewsAndInsightsHub />} />
          <Route path="/news-and-insights/:slug" element={<NewsAndInsightsArticle />} />
          <Route path="/news-and-insights/:year/:slug" element={<NewsAndInsightsArticle />} />
          <Route
            path="/mobilecorex-partners-with-wireless-technology-labs-wtl-to-deploy-core-network"
            element={<Navigate to="/news-and-insights/2024/mobilecorex-partners-with-wireless-technology-labs-wtl-to-deploy-core-network" replace />}
          />
          <Route
            path="/mobilecorex-partners-with-tecnotree-to-launch-multi-MVNO-mvne-bss-platform"
            element={<Navigate to="/news-and-insights/2024/mobilecorex-partners-with-tecnotree-to-launch-multi-MVNO-mvne-bss-platform" replace />}
          />
          <Route
            path="/mobilecorex-highlights-innovations-at-mvnos-world-congress-2024-in-brussels"
            element={<Navigate to="/news-and-insights/2024/mobilecorex-highlights-innovations-at-mvnos-world-congress-2024-in-brussels" replace />}
          />
          <Route path="/news-and-events" element={<Navigate to="/news-and-insights" replace />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/legal/privacy-policy" element={<LegalPrivacy />} />
          <Route path="/legal/terms-of-service" element={<LegalTerms />} />
          <Route path="/legal/cookie-policy" element={<LegalCookie />} />
          <Route path="/legal/accessibility" element={<LegalAccessibility />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </PageLayout>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <PlausibleAnalytics />
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
